import type { CompetitionDetail, ResultsGrid } from "../api";

export interface ExportCell {
  /** Team short name, or null for no pick / not yet revealed. */
  label: string | null;
  outcome: "pending" | "win" | "loss" | "void" | null;
  hidden: boolean;
}

export interface ExportRow {
  displayName: string;
  status: "alive" | "out";
  cells: ExportCell[];
}

export interface ExportData {
  title: string;
  subtitle: string;
  generatedAt: Date;
  roundLabels: string[];
  rows: ExportRow[];
}

/** Reshapes the API's flat cell list into the row-major grid both export renderers draw from. */
export function buildExportData(
  competition: Pick<CompetitionDetail, "name" | "league" | "season">,
  grid: ResultsGrid,
): ExportData {
  const cellByKey = new Map(grid.cells.map((cell) => [`${cell.entryId}:${cell.roundId}`, cell]));

  const rows: ExportRow[] = [...grid.entries]
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === "alive" ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    })
    .map((entry) => ({
      displayName: entry.displayName,
      status: entry.status,
      cells: grid.rounds.map((round) => {
        const cell = cellByKey.get(`${entry.entryId}:${round.id}`);
        if (!cell || !cell.hasPicked) return { label: null, outcome: null, hidden: false };
        if (!cell.team) return { label: null, outcome: null, hidden: true };
        return { label: cell.team.shortName, outcome: cell.outcome, hidden: false };
      }),
    }));

  return {
    title: competition.name,
    subtitle: `${competition.league.name} · ${competition.season.label}`,
    generatedAt: new Date(),
    roundLabels: grid.rounds.map((r) => `R${r.roundNumber}`),
    rows,
  };
}
